import { api } from '@/api/http';
import { Spinner } from '@/components/ui/spinner';
import { $setFeatures } from '@/entities/features-entity';
import { $nickname, $profile, $setNickname, $setProfile } from '@/entities/profile-entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { seedDevData } from '@/mocks/seed-dev-data';
import { useUnit } from 'effector-react';
import { useEffect, useState, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';

if (isDevMode) {
	seedDevData();
}

export default function AuthProvider({ children }: PropsWithChildren) {
	const [setNickname, nickname, profile, setProfile, setFeatures] = useUnit([
		$setNickname,
		$nickname,
		$profile,
		$setProfile,
		$setFeatures,
	]);
	const navigate = useNavigate();
	const isPublic = location.pathname === '/auth';
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		async function bootstrapAuth() {
			try {
				const res = await api.get('/me');
				if (cancelled) return;
				setProfile(res.data.profile);
				setNickname(res.data.profile.nickname);
				if (res.data.features) {
					setFeatures(res.data.features);
				}
			} catch {
				if (cancelled) return;

				const initData = isDevMode ? '' : window.Telegram?.WebApp?.initData;
				if (!isDevMode && !initData) return;

				try {
					const res = await api.post('/get-profile-name', { initData: initData ?? '' });
					if (!cancelled && res.data.nickname) {
						setNickname(res.data.nickname);
					}
				} catch {
					// nickname stays empty; spinner resolves below
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void bootstrapAuth();

		return () => {
			cancelled = true;
		};
	}, [setNickname, setProfile, setFeatures]);

	useEffect(() => {
		if (nickname && !profile && !isPublic && !isLoading) {
			navigate('/auth');
		}
	}, [nickname, profile, isPublic, isLoading, navigate]);

	if (nickname && !profile && !isPublic && !isLoading) {
		return <></>;
	}

	if (isLoading || !nickname) {
		return (
			<div className='h-full w-full min-h-0 flex flex-col overflow-hidden'>
				<div className='flex-1 flex items-center justify-center'>
					<Spinner className='w-[50px] h-[50px]' />
				</div>
			</div>
		);
	}

	return <div className='h-full w-full min-h-0 flex flex-col overflow-hidden'>{children}</div>;
}
